import * as React from 'react';
import i18next from 'i18next';
import { LANGUAGES, ResourceService } from '@ixicare/resources';

import getNavigatorLanguage from './utils/getBrowserLanguage';

const LANGUAGE_CODES = LANGUAGES.map((l) => l.code);

const loadTranslations = () => {
  const modules = import.meta.glob('./data/*.json', { eager: true }) as Record<string, { default: any }>;
  const translations: Record<string, any> = {};

  for (const code of LANGUAGE_CODES) {
    const mod = modules[`./data/${code}.json`];
    if (mod) {
      translations[code] = mod.default;
    }
  }

  return translations;
};

const supportedLanguages = loadTranslations();

const DEFAULT_LANGUAGE = 'en';
const STORAGE_KEY = 'hcp-dashboard.language';

const resolveBrowserLanguage = (): string => {
  const deviceLanguage = getNavigatorLanguage();
  if (!deviceLanguage) return DEFAULT_LANGUAGE;
  if (supportedLanguages[deviceLanguage]) return deviceLanguage;
  const base = deviceLanguage.split('-')[0];
  if (supportedLanguages[base]) return base;
  return DEFAULT_LANGUAGE;
};

// Module-scope `t(...)` calls (menu, route configs, etc.) evaluate before Auth
// fetches the user, so seed i18next from a cached code in localStorage to keep
// those strings consistent across reloads.
const resolveInitialLanguage = (): string => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && supportedLanguages[stored]) return stored;
  } catch {
    // localStorage may be unavailable (private mode, etc.) — fall through.
  }
  return resolveBrowserLanguage();
};

export const getCurrentLanguage = () => resolveInitialLanguage();

const resources: Record<string, { translation: any }> = {};
for (const [code, data] of Object.entries(supportedLanguages)) {
  resources[code] = { translation: data };
}

i18next.init({
  resources,
  lng: resolveInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false,
  },
});

ResourceService.setLanguage(i18next.language);

// Switch the active UI language and sync the Accept-Language header for API
// calls. The chosen code is cached in localStorage — that cache is the source
// of truth for the dashboard, so the next page load reads it back before any
// module-scope `t(...)` evaluates.
export const setLanguage = (code?: string | null) => {
  const next = code && supportedLanguages[code] ? code : resolveBrowserLanguage();
  if (i18next.language !== next) {
    i18next.changeLanguage(next);
  }
  ResourceService.setLanguage(next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // ignore — non-critical persistence.
  }
};

export const hasStoredLanguage = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return !!(stored && supportedLanguages[stored]);
  } catch {
    return false;
  }
};

export const t = (key: string, properties?: any): string => {
  let translation = i18next.t(key, properties) as string;
  translation = translation.replace(/\<\/?b\>/gi, '').replace(/\<\/?strong\>/gi, '');
  return translation;
};
export const tHtml = (key: string, properties?: any): any => {
  return <span dangerouslySetInnerHTML={{ __html: i18next.t(key, properties) }} />;
};
export default i18next;
